import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, User, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import DemoForm from "@/components/forms/DemoForm";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog/${slug}`);
        setArticle(response.data);
        document.title = `${response.data.title} | Blog Zimoo`;
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-[#404040]">Cargando artículo...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#404040] mb-4">Artículo no encontrado</p>
          <Link to="/blog">
            <Button className="btn-primary">Volver al Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="section-padding bg-gradient-to-b from-[#FAFAFA] to-white">
        <div className="max-w-4xl mx-auto container-padding">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#404040] hover:text-[#1ABCB4] mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Volver al Blog
          </Link>
          
          <span className="text-sm text-[#1ABCB4] font-medium">{article.category}</span>
          <h1 className="heading-1 mt-2 mb-6">{article.title}</h1>
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-[#404040] mb-8">
            <span className="flex items-center gap-2">
              <User className="h-4 w-4" />
              {article.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {new Date(article.date).toLocaleDateString('es-MX', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {article.read_time} de lectura
            </span>
          </div>
          
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-[400px] object-cover rounded-2xl"
          />
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <article 
                className="prose prose-lg max-w-none prose-headings:text-[#0A0A0A] prose-headings:font-semibold prose-p:text-[#404040] prose-p:leading-relaxed prose-a:text-[#1ABCB4] prose-strong:text-[#0A0A0A] prose-li:text-[#404040]"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
              
              {/* Share */}
              <div className="mt-12 pt-8 border-t border-black/5">
                <div className="flex items-center gap-4">
                  <Share2 className="h-5 w-5 text-[#404040]" />
                  <span className="text-[#404040]">Compartir este artículo:</span>
                  <div className="flex gap-2">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-[#FAFAFA] flex items-center justify-center hover:bg-[#1ABCB4] hover:text-white transition-colors"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-[#FAFAFA] flex items-center justify-center hover:bg-[#1ABCB4] hover:text-white transition-colors"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
              
              {/* CTA in article */}
              <div className="mt-12 bg-gradient-to-r from-[#1ABCB4]/5 to-[#5200FF]/5 rounded-2xl p-8 border border-[#1ABCB4]/10">
                <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3">
                  ¿Listo para optimizar tus ingresos?
                </h3>
                <p className="text-[#404040] mb-6">
                  Zimoo te ayuda a implementar las mejores prácticas de revenue management de forma automatizada. Solicita una demo gratuita.
                </p>
                <Link to="/#demo-form">
                  <Button className="btn-primary">
                    Solicitar Demo Gratuita
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-[#FAFAFA] rounded-xl p-6 border border-black/5">
                  <h4 className="font-semibold text-[#0A0A0A] mb-4">Contenido Relacionado</h4>
                  <ul className="space-y-3">
                    <li>
                      <Link to="/revenue-management-hoteles" className="text-sm text-[#404040] hover:text-[#1ABCB4] transition-colors">
                        Qué es Revenue Management
                      </Link>
                    </li>
                    <li>
                      <Link to="/pricing-dinamico" className="text-sm text-[#404040] hover:text-[#1ABCB4] transition-colors">
                        Pricing Dinámico
                      </Link>
                    </li>
                    <li>
                      <Link to="/forecasting-hotelero" className="text-sm text-[#404040] hover:text-[#1ABCB4] transition-colors">
                        Forecasting Hotelero
                      </Link>
                    </li>
                    <li>
                      <Link to="/revpar" className="text-sm text-[#404040] hover:text-[#1ABCB4] transition-colors">
                        ¿Qué es RevPAR?
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-text mb-4 block">SIGUIENTE PASO</span>
              <h2 className="heading-2 mb-6">
                Implementa estas estrategias con Zimoo
              </h2>
              <p className="body-text">
                Deja que nuestro software automatice las mejores prácticas de revenue management para tu hotel. Solicita una demo personalizada.
              </p>
            </div>
            <DemoForm sourcePage={`blog-${slug}`} />
          </div>
        </div>
      </section>
    </div>
  );
}
